import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Typography,
  Paper,
  Grid,
  Box,
  Backdrop,
  CircularProgress,
  Fade,
  Grow,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
} from "@material-ui/core";
import clsx from "clsx";
import moment from "moment";

import DirectionsCarIcon from "@material-ui/icons/DirectionsCar";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WarningIcon from "@material-ui/icons/Warning";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";

import { useDispatch, useSelector } from "react-redux";
import { getFleetByLocation, getVehicleSigns } from "../../actions/vehicleAction";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    background: theme.palette.type === "dark" 
      ? "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)" 
      : "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
    minHeight: "100vh",
  },
  header: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2.5),
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    borderRadius: 12,
    boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
  },
  title: {
    fontWeight: 900,
    fontSize: 24,
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    opacity: 0.9,
    fontSize: 20,
  },
  statsContainer: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  statCard: {
    padding: theme.spacing(1),
    borderRadius: 12,
    background: theme.palette.type === "dark" ? "#2d2d2d" : "#fff",
    boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
  },
  statsGrid: {
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    gap: theme.spacing(3),
  },
  statItem: {
    textAlign: "center",
    flex: 1,
    position: "relative",
    "&:not(:last-child)::after": {
      content: '""',
      position: "absolute",
      right: -theme.spacing(1.5),
      top: "50%",
      transform: "translateY(-50%)",
      height: "60%",
      width: 2,
      backgroundColor: theme.palette.divider,
      opacity: 2,
    },
  },
  statNumber: {
    fontSize: 32,
    fontWeight: 900,
    marginBottom: theme.spacing(0.5),
  },
  statLabel: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    fontWeight: 600,
    textTransform: "uppercase",
  },
  tableContainer: {
    borderRadius: 12,
    boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
    background: theme.palette.type === "dark" ? "#2d2d2d" : "#fff",
  },
  table: {
    minWidth: 650,
  },
  tableHead: {
    background: theme.palette.type === "dark" 
      ? "linear-gradient(135deg, #434343 0%, #000000 100%)"
      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },
  tableHeadCell: {
    color: "#fff !important",
    fontWeight: 900,
    fontSize: 13,
    textTransform: "uppercase",
    padding: "12px 16px",
  },
  tableRow: {
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      transform: "scale(1.01)",
    },
  },
  tableRowAvailable: {
    borderLeft: "4px solid #56ab2f",
    backgroundColor: "rgba(86, 171, 47, 0.05)",
  },
  tableRowInUse: {
    borderLeft: "4px solid #eb3349",
    backgroundColor: "rgba(235, 51, 73, 0.05)",
  },
  tableRowService: {
    borderLeft: "4px solid #f093fb",
    backgroundColor: "rgba(240, 147, 251, 0.05)",
  },
  tableCell: {
    padding: "10px 16px",
    fontSize: 13,
  },
  vehicleName: {
    fontWeight: 700,
    fontSize: 14,
  },
  statusChip: {
    fontWeight: 700,
    fontSize: 11,
    height: 28,
  },
  statusAvailable: {
    backgroundColor: "#56ab2f",
    color: "#fff",
  },
  statusInUse: {
    backgroundColor: "#eb3349",
    color: "#fff",
  },
  statusService: {
    backgroundColor: "#f093fb",
    color: "#fff",
  },
  statusUnknown: {
    backgroundColor: "#9e9e9e",
    color: "#fff",
  },
  operatorCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  smallAvatar: {
    width: 32,
    height: 32,
    fontSize: 14,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
  refreshInfo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    color: "rgba(255,255,255,0.7)",
    fontSize: 11,
  },
  pulseDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "#4caf50",
    animation: "$pulse 2s infinite",
  },
  "@keyframes pulse": {
    "0%": {
      opacity: 1,
      transform: "scale(1)",
    },
    "50%": {
      opacity: 0.5,
      transform: "scale(1.3)",
    },
    "100%": {
      opacity: 1,
      transform: "scale(1)",
    },
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(8),
    color: theme.palette.text.secondary,
  },
  emptyIcon: {
    fontSize: 80,
    marginBottom: theme.spacing(2),
    opacity: 0.3,
  },
  headerDateTime: {
    textAlign: "right",
  },
  dateText: {
    fontSize: 24,
    fontWeight: 700,
  },
  timeText: {
    fontSize: 20,
    fontWeight: 600,
  },
  searchContainer: {
    marginBottom: theme.spacing(2),
  },
  searchField: {
    backgroundColor: theme.palette.type === "dark" ? "#2d2d2d" : "#fff",
    borderRadius: 12,
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
    },
  },
}));

const VehicleSignDashboard = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [visible, setVisible] = useState(false);
  const [search, setSearch] = useState("");

  const { loading, locFleets, vehicleSigns } = useSelector((state) => state.vehicle);
  const { profile } = useSelector((state) => state.users);

  useEffect(() => {
    setVisible(true);
    if (profile && profile.location) {
      dispatch(getFleetByLocation(profile.location._id));
      dispatch(getVehicleSigns());
    }
  }, [dispatch, profile]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      if (profile && profile.location) {
        dispatch(getFleetByLocation(profile.location._id));
        dispatch(getVehicleSigns());
      }
    }, 300000);

    return () => clearInterval(interval);
  }, [dispatch, profile]);

  const getVehicleStatus = (vehicle) => {
    if (!vehicleSigns || vehicleSigns.length === 0) {
      return {
        status: "Available",
        operator: null,
        timestamp: null,
        signType: null,
        jobSite: null,
        jobNumber: null,
      };
    }

    // Get all signs for this vehicle
    const vehicleSignRecords = vehicleSigns.filter(
      (sign) => sign.vehicle && sign.vehicle._id === vehicle._id
    );

    if (vehicleSignRecords.length === 0) {
      return {
        status: "Available",
        operator: null,
        timestamp: null,
        signType: null,
        jobSite: null,
        jobNumber: null,
      };
    }

    // Get the most recent sign
    const latestSign = vehicleSignRecords.sort((a, b) => {
      const dateA = a.signType === "Sign In" ? new Date(a.signedInAt) : new Date(a.signedOutAt);
      const dateB = b.signType === "Sign In" ? new Date(b.signedInAt) : new Date(b.signedOutAt);
      return dateB - dateA;
    })[0];

    return {
      status: latestSign.signType === "Sign Out" ? "In Use" : "Available",
      operator: latestSign.operator,
      timestamp: latestSign.signType === "Sign In" ? latestSign.signedInAt : latestSign.signedOutAt,
      signType: latestSign.signType,
      jobSite: latestSign.jobSite,
      jobNumber: latestSign.jobNumber,
    };
  };

  const updateSearch = (e) => {
    setSearch(e.target.value);
  };

  const clearSearch = () => {
    setSearch("");
  };

  const vehicles = locFleets || [];
  
  // Filter vehicles based on search
  const filteredVehicles = vehicles.filter((vehicle) => {
    if (search === "") return true;
    
    const statusInfo = getVehicleStatus(vehicle);
    const searchLower = search.toLowerCase();
    
    return (
      vehicle.displayname?.toLowerCase().includes(searchLower) ||
      vehicle.name?.toLowerCase().includes(searchLower) ||
      vehicle.unitNumber?.toLowerCase().includes(searchLower) ||
      vehicle.licencePlate?.toLowerCase().includes(searchLower) ||
      statusInfo.operator?.toLowerCase().includes(searchLower) ||
      statusInfo.jobSite?.toLowerCase().includes(searchLower) ||
      statusInfo.jobNumber?.toLowerCase().includes(searchLower) ||
      statusInfo.status?.toLowerCase().includes(searchLower)
    );
  });

  // // Sort vehicles by latest activity first
  // const sortedVehicles = [...filteredVehicles].sort((a, b) => {
  //   const statusA = getVehicleStatus(a);
  //   const statusB = getVehicleStatus(b);
    
  //   // If both have timestamps, sort by most recent first
  //   if (statusA.timestamp && statusB.timestamp) {
  //     return new Date(statusB.timestamp) - new Date(statusA.timestamp);
  //   }
    
  //   // Vehicles with activity come before those without
  //   if (statusA.timestamp && !statusB.timestamp) return -1;
  //   if (!statusA.timestamp && statusB.timestamp) return 1;
    
  //   // If neither has activity, maintain original order
  //   return 0;
  // });

  //filter and sort alphabetically
  const sortedVehicles = [...filteredVehicles].filter(fleet => fleet.location !== undefined).sort((a, b) => {
    return a.displayname.localeCompare(b.displayname);
  });

  const availableCount = sortedVehicles.filter((v) => {
    const status = getVehicleStatus(v);
    return status.status === "Available";
  }).length;
  const inUseCount = sortedVehicles.filter((v) => getVehicleStatus(v).status === "In Use").length;

  const getStatusChipClass = (status) => {
    if (status === "In Use") return classes.statusInUse;
    if (status === "Available") return classes.statusAvailable;
    if (status.includes("Service") || status.includes("Repair")) return classes.statusService;
    return classes.statusUnknown;
  };

  const getRowClass = (status) => {
    if (status === "In Use") return clsx(classes.tableRow, classes.tableRowInUse);
    if (status === "Available") return clsx(classes.tableRow, classes.tableRowAvailable);
    if (status.includes("Service") || status.includes("Repair")) return clsx(classes.tableRow, classes.tableRowService);
    return classes.tableRow;
  };

  const getStatusIcon = (status) => {
    if (status === "In Use") return <WarningIcon style={{ fontSize: 16 }} />;
    return <CheckCircleIcon style={{ fontSize: 16 }} />;
  };

  return (
    <div className={classes.root}>
      <Fade in={visible} timeout={800}>
        <Paper className={classes.header}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography className={classes.title}>
                🚗 Vehicle Dashboard
              </Typography>
              <Typography className={classes.subtitle}>
                {profile?.location?.displayname || "All Locations"} - Live Status
              </Typography>
            </Grid>
            <Grid item className={classes.headerDateTime}>
              <Typography className={classes.dateText}>
                {moment().format("ddd, MMM DD, YYYY")}
              </Typography>
              <Typography className={classes.timeText}>
                {moment().format("h:mm A")}
              </Typography>
            </Grid>
          </Grid>
          {/* Consolidated Statistics Card */}
        <Grid container spacing={2} className={classes.statsContainer}>
            <Grid item xs={12}>
            <Grow in={visible} timeout={600}>
                <Paper className={classes.statCard}>
                <Box className={classes.statsGrid}>
                    <Box className={classes.statItem}>
                    <Typography className={classes.statLabel}>Total</Typography>
                    <Typography
                        className={classes.statNumber}
                        style={{
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        }}
                    >
                        {sortedVehicles.length}
                    </Typography>
                    </Box>

                    <Box className={classes.statItem}>
                    <Typography className={classes.statLabel}>Available</Typography>
                    <Typography
                        className={classes.statNumber}
                        style={{
                        background: "linear-gradient(135deg, #56ab2f 0%, #a8e063 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        }}
                    >
                        {availableCount}
                    </Typography>
                    </Box>

                    <Box className={classes.statItem}>
                    <Typography className={classes.statLabel}>In Use</Typography>
                    <Typography
                        className={classes.statNumber}
                        style={{
                        background: "linear-gradient(135deg, #eb3349 0%, #f45c43 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        }}
                    >
                        {inUseCount}
                    </Typography>
                    </Box>
                </Box>
                </Paper>
            </Grow>
            </Grid>
        </Grid>
          <Box className={classes.refreshInfo}>
            <div className={classes.pulseDot} />
            <Typography variant="caption">Auto-refreshing every 5 minutes</Typography>
          </Box>
        </Paper>
      </Fade>

      {/* Search Field */}
      <Grid container spacing={2} className={classes.searchContainer}>
        <Grid item xs={12}>
          <Grow in={visible} timeout={800}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by vehicle name, unit #, operator, location, or status..."
              value={search}
              onChange={updateSearch}
              className={classes.searchField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <Chip
                      label={`${sortedVehicles.length} ${sortedVehicles.length === 1 ? 'result' : 'results'}`}
                      size="small"
                      color="primary"
                      style={{ marginRight: 8 }}
                    />
                    <ClearIcon
                      style={{ cursor: "pointer", color: "#999" }}
                      onClick={clearSearch}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grow>
        </Grid>
      </Grid>

      {/* Vehicle Table */}
      <Grow in={visible} timeout={1000}>
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table className={classes.table} size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classes.tableHeadCell}>Vehicle</TableCell>
                <TableCell className={classes.tableHeadCell}>Unit #</TableCell>
                <TableCell className={classes.tableHeadCell}>Status</TableCell>
                <TableCell className={classes.tableHeadCell}>Last Action</TableCell>
                <TableCell className={classes.tableHeadCell}>Operator</TableCell>
                <TableCell className={classes.tableHeadCell}>Job #</TableCell>
                <TableCell className={classes.tableHeadCell}>Date & Time</TableCell>
                <TableCell className={classes.tableHeadCell}>Current Location</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedVehicles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" style={{ padding: 40 }}>
                    <DirectionsCarIcon className={classes.emptyIcon} />
                    <Typography variant="h6">
                      {search ? "No vehicles found" : "No vehicles available"}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {search 
                        ? `No vehicles match "${search}"`
                        : "No vehicles are registered at this location"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedVehicles.map((vehicle, index) => {
                  const statusInfo = getVehicleStatus(vehicle);
                  return (
                    <TableRow key={vehicle._id} className={getRowClass(statusInfo.status)}>
                      <TableCell className={classes.tableCell}>
                        <Typography className={classes.vehicleName}>
                          {vehicle.displayname || vehicle.name}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {vehicle.unitNumber || vehicle.licencePlate || "-"}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Chip
                          label={statusInfo.status}
                          icon={getStatusIcon(statusInfo.status)}
                          className={clsx(classes.statusChip, getStatusChipClass(statusInfo.status))}
                          size="small"
                        />
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {statusInfo.signType || "-"}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {statusInfo.operator ? (
                          <Box className={classes.operatorCell}>
                            <Avatar className={classes.smallAvatar}>
                              {statusInfo.operator.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2">{statusInfo.operator}</Typography>
                          </Box>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {statusInfo.jobNumber || "-"}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {statusInfo.timestamp
                          ? moment(statusInfo.timestamp).format("MMM DD, h:mm A")
                          : "-"}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {statusInfo.jobSite || "-"}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grow>

      <Backdrop className={classes.backdrop} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  );
};

export default VehicleSignDashboard;
